import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  AddPhotoAlternate as AddPhotoAlternateIcon,
  ChangeHistory as ChangeHistoryIcon,
  CropSquare as CropSquareIcon,
  DeleteOutline as DeleteOutlineIcon,
  RestartAlt as RestartAltIcon,
  SaveAlt as SaveAltIcon,
} from '@mui/icons-material'
import { Rnd } from 'react-rnd'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { collection, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore'
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { useAuth } from '../contexts/AuthContext'
import { db, storage } from '../firebase'

const MIN_RECT_SIZE = 12
const MIN_POLYGON_POINTS = 3
const POLYGON_CLOSE_DISTANCE = 14
const DEFAULT_COLORS = ['#ff6b6b', '#4dabf7', '#51cf66', '#fcc419', '#845ef7', '#f06595']
const EMPTY_IMAGE_BOUNDS = { width: 0, height: 0, naturalWidth: 0, naturalHeight: 0 }
const DEFAULT_DEFINITION_TITLE = 'Yeni AOI tanimi'

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const toDraftRect = (startPoint, currentPoint, bounds) => {
  const startX = clamp(startPoint.x, 0, bounds.width)
  const startY = clamp(startPoint.y, 0, bounds.height)
  const endX = clamp(currentPoint.x, 0, bounds.width)
  const endY = clamp(currentPoint.y, 0, bounds.height)

  return {
    x: Math.min(startX, endX),
    y: Math.min(startY, endY),
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY),
  }
}

const getPolygonBounds = (points) => {
  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)

  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  }
}

const isPointNear = (pointA, pointB, threshold = POLYGON_CLOSE_DISTANCE) =>
  Math.hypot(pointA.x - pointB.x, pointA.y - pointB.y) <= threshold

const toSvgPointString = (points) => points.map((point) => `${point.x},${point.y}`).join(' ')

const createAreaBase = (areaCount) => ({
  id: `aoi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: `AOI ${areaCount + 1}`,
  description: '',
  color: DEFAULT_COLORS[areaCount % DEFAULT_COLORS.length],
})

const buildRectArea = (rect, bounds, areaCount) => ({
  ...createAreaBase(areaCount),
  shape: 'rect',
  x: rect.x / bounds.width,
  y: rect.y / bounds.height,
  width: rect.width / bounds.width,
  height: rect.height / bounds.height,
})

const buildPolygonArea = (points, bounds, areaCount) => {
  const normalizedPoints = points.map((point) => ({
    x: point.x / bounds.width,
    y: point.y / bounds.height,
  }))

  return {
    ...createAreaBase(areaCount),
    shape: 'polygon',
    points: normalizedPoints,
    ...getPolygonBounds(normalizedPoints),
  }
}

const createImageItem = (file) => ({
  id: `image-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: file.name,
  src: URL.createObjectURL(file),
  file,
  storagePath: '',
  naturalWidth: 0,
  naturalHeight: 0,
  areas: [],
})

const formatPolygonPoints = (points, width, height) =>
  `[${points
    .map((point) => `(${Math.round(point.x * width)}, ${Math.round(point.y * height)})`)
    .join(', ')}]`

const toRectExportPoints = (area) => [
  { x: area.x, y: area.y },
  { x: area.x + area.width, y: area.y },
  { x: area.x + area.width, y: area.y + area.height },
  { x: area.x, y: area.y + area.height },
]

const getAreaExportPoints = (area) =>
  area.shape === 'polygon' ? area.points : toRectExportPoints(area)

const buildAreaExportLines = (image, startIndex = 0) => {
  if (!image.naturalWidth || !image.naturalHeight) {
    return []
  }

  return image.areas.map(
    (area, index) =>
      `Aoi${startIndex + index + 1} = ${formatPolygonPoints(
        getAreaExportPoints(area),
        image.naturalWidth,
        image.naturalHeight
      )}`
  )
}

const downloadTextFile = (content, fileName) => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const downloadUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = downloadUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(downloadUrl)
}

const toExportFileName = (name) => {
  const baseName = (name || 'aoi_polygon')
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-z0-9-_]+/gi, '_')
    .replace(/^_+|_+$/g, '')

  return `${baseName || 'aoi_polygon'}_polygon.txt`
}

const createDefinitionTitle = () =>
  `${DEFAULT_DEFINITION_TITLE} ${new Date().toLocaleDateString('tr-TR')}`

const sanitizeStorageSegment = (value) =>
  (value || 'file').replace(/[^a-z0-9._-]+/gi, '_')

const hydrateStoredArea = (area, index) => ({
  id: area?.id || `aoi-${Date.now()}-${index}`,
  name: area?.name || `AOI ${index + 1}`,
  description: area?.description || '',
  color: area?.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
  shape: area?.shape === 'polygon' ? 'polygon' : 'rect',
  x: Number(area?.x || 0),
  y: Number(area?.y || 0),
  width: Number(area?.width || 0),
  height: Number(area?.height || 0),
  points: Array.isArray(area?.points)
    ? area.points.map((point) => ({
        x: Number(point?.x || 0),
        y: Number(point?.y || 0),
      }))
    : undefined,
})

const hydrateStoredImage = (image, index) => ({
  id: image?.id || `image-${Date.now()}-${index}`,
  name: image?.name || `Image ${index + 1}`,
  src: image?.src || '',
  file: null,
  storagePath: image?.storagePath || '',
  naturalWidth: Number(image?.naturalWidth || 0),
  naturalHeight: Number(image?.naturalHeight || 0),
  areas: Array.isArray(image?.areas)
    ? image.areas.map((area, areaIndex) => hydrateStoredArea(area, areaIndex))
    : [],
})

const serializeArea = (area) => ({
  id: area.id,
  name: area.name || '',
  description: area.description || '',
  color: area.color || DEFAULT_COLORS[0],
  shape: area.shape === 'polygon' ? 'polygon' : 'rect',
  x: Number(area.x || 0),
  y: Number(area.y || 0),
  width: Number(area.width || 0),
  height: Number(area.height || 0),
  ...(area.shape === 'polygon'
    ? {
        points: Array.isArray(area.points)
          ? area.points.map((point) => ({
              x: Number(point.x || 0),
              y: Number(point.y || 0),
            }))
          : [],
      }
    : {}),
})

const serializeImage = (image) => ({
  id: image.id,
  name: image.name,
  src: image.src,
  storagePath: image.storagePath || '',
  naturalWidth: Number(image.naturalWidth || 0),
  naturalHeight: Number(image.naturalHeight || 0),
  areas: image.areas.map(serializeArea),
})

const formatSavedAt = (value, language) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return ''
  }

  return new Intl.DateTimeFormat(language || 'tr-TR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(value)
}

export default function AoiDefinitionPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { definitionId: routeDefinitionId } = useParams()
  const { currentUser } = useAuth()
  const fileInputRef = useRef(null)
  const imageRef = useRef(null)
  const objectUrlsRef = useRef(new Set())
  const deletedStoragePathsRef = useRef(new Set())

  const [definitionTitle, setDefinitionTitle] = useState(createDefinitionTitle)
  const [images, setImages] = useState([])
  const [selectedImageId, setSelectedImageId] = useState(null)
  const [imageBounds, setImageBounds] = useState(EMPTY_IMAGE_BOUNDS)
  const [selectedAreaId, setSelectedAreaId] = useState(null)
  const [drawingMode, setDrawingMode] = useState('rect')
  const [drawingStart, setDrawingStart] = useState(null)
  const [draftRect, setDraftRect] = useState(null)
  const [draftPolygon, setDraftPolygon] = useState([])
  const [polygonCursor, setPolygonCursor] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const [isLoadingDefinition, setIsLoadingDefinition] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState(null)

  const selectedImage = images.find((image) => image.id === selectedImageId) || null
  const imageSrc = selectedImage?.src || ''
  const imageName = selectedImage?.name || ''
  const areas = selectedImage?.areas || []
  const selectedArea = areas.find((area) => area.id === selectedAreaId) || null
  const totalAreaCount = images.reduce((count, image) => count + image.areas.length, 0)
  const pageTitle = routeDefinitionId
    ? t('aoiPage.editTitle', { defaultValue: 'Kayitli AOI tanimi' })
    : t('aoiPage.newTitle', { defaultValue: 'Yeni AOI tanimi' })

  const releaseObjectUrl = useCallback((src) => {
    if (!src || !objectUrlsRef.current.has(src)) {
      return
    }

    URL.revokeObjectURL(src)
    objectUrlsRef.current.delete(src)
  }, [])

  const replaceImages = useCallback((nextImages) => {
    setImages((prevImages) => {
      const nextSources = new Set(nextImages.map((image) => image.src))

      prevImages.forEach((image) => {
        if (!nextSources.has(image.src)) {
          releaseObjectUrl(image.src)
        }
      })

      return nextImages
    })
  }, [releaseObjectUrl])

  const setFeedbackMessage = useCallback((message, severity = 'info') => {
    setFeedback(message ? { message, severity } : null)
  }, [])

  useEffect(() => {
    const objectUrls = objectUrlsRef.current

    return () => {
      objectUrls.forEach((src) => {
        URL.revokeObjectURL(src)
      })
      objectUrls.clear()
    }
  }, [])

  const resetDrafts = useCallback(() => {
    setDrawingStart(null)
    setDraftRect(null)
    setDraftPolygon([])
    setPolygonCursor(null)
  }, [])

  const resetDefinitionState = useCallback(() => {
    replaceImages([])
    setDefinitionTitle(createDefinitionTitle())
    setSelectedImageId(null)
    setImageBounds(EMPTY_IMAGE_BOUNDS)
    setSelectedAreaId(null)
    setDrawingMode('rect')
    resetDrafts()
    setFeedback(null)
    setIsSaving(false)
    setHasUnsavedChanges(false)
    setLastSavedAt(null)
    deletedStoragePathsRef.current.clear()
  }, [replaceImages, resetDrafts])

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges) {
        return
      }

      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  useEffect(() => {
    if (!currentUser?.uid) {
      return
    }

    if (!routeDefinitionId) {
      setIsLoadingDefinition(false)
      resetDefinitionState()
      return
    }

    let isMounted = true

    const loadDefinition = async () => {
      setIsLoadingDefinition(true)
      setFeedback(null)

      try {
        const definitionSnapshot = await getDoc(doc(db, 'aoiDefinitions', routeDefinitionId))

        if (!definitionSnapshot.exists()) {
          throw new Error('missing-definition')
        }

        const definitionData = definitionSnapshot.data()

        if (definitionData.ownerId && definitionData.ownerId !== currentUser.uid) {
          throw new Error('forbidden-definition')
        }

        if (!isMounted) {
          return
        }

        const nextImages = Array.isArray(definitionData.images)
          ? definitionData.images.map((image, index) => hydrateStoredImage(image, index))
          : []

        replaceImages(nextImages)
        setDefinitionTitle(definitionData.title || createDefinitionTitle())
        setSelectedImageId(nextImages[0]?.id || null)
        setSelectedAreaId(null)
        setImageBounds(EMPTY_IMAGE_BOUNDS)
        setDrawingMode('rect')
        resetDrafts()
        setHasUnsavedChanges(false)
        setLastSavedAt(
          definitionData.updatedAt?.toDate?.() || definitionData.createdAt?.toDate?.() || null
        )
        deletedStoragePathsRef.current.clear()
      } catch (error) {
        console.error('AOI tanimi yuklenirken hata:', error)

        if (!isMounted) {
          return
        }

        resetDefinitionState()
        setFeedbackMessage(
          error.message === 'forbidden-definition'
            ? t('aoiPage.loadForbidden', {
                defaultValue: 'Bu AOI kaydina erisim izniniz yok.',
              })
            : t('aoiPage.loadError', {
                defaultValue: 'AOI tanimi yuklenemedi.',
              }),
          'error'
        )
      } finally {
        if (isMounted) {
          setIsLoadingDefinition(false)
        }
      }
    }

    loadDefinition()

    return () => {
      isMounted = false
    }
  }, [
    currentUser?.uid,
    replaceImages,
    resetDefinitionState,
    resetDrafts,
    routeDefinitionId,
    setFeedbackMessage,
    t,
  ])

  const selectImage = (imageId) => {
    if (imageId === selectedImageId) {
      return
    }

    setSelectedImageId(imageId)
    setSelectedAreaId(null)
    setImageBounds(EMPTY_IMAGE_BOUNDS)
    resetDrafts()
    setFeedback(null)
  }

  const updateSelectedImage = useCallback((updater) => {
    if (!selectedImageId) {
      return
    }

    setImages((prev) =>
      prev.map((image) => {
        if (image.id !== selectedImageId) {
          return image
        }

        return updater(image)
      })
    )
  }, [selectedImageId])

  const setAreasForSelectedImage = useCallback((updater) => {
    updateSelectedImage((image) => ({
      ...image,
      areas: typeof updater === 'function' ? updater(image.areas) : updater,
    }))
  }, [updateSelectedImage])

  const refreshImageBounds = useCallback(() => {
    if (!imageRef.current) {
      setImageBounds(EMPTY_IMAGE_BOUNDS)
      return
    }

    const nextBounds = {
      width: imageRef.current.clientWidth,
      height: imageRef.current.clientHeight,
      naturalWidth: imageRef.current.naturalWidth || selectedImage?.naturalWidth || 0,
      naturalHeight: imageRef.current.naturalHeight || selectedImage?.naturalHeight || 0,
    }

    setImageBounds((prev) => {
      if (
        prev.width === nextBounds.width &&
        prev.height === nextBounds.height &&
        prev.naturalWidth === nextBounds.naturalWidth &&
        prev.naturalHeight === nextBounds.naturalHeight
      ) {
        return prev
      }

      return nextBounds
    })

    if (
      selectedImageId &&
      (selectedImage?.naturalWidth !== nextBounds.naturalWidth ||
        selectedImage?.naturalHeight !== nextBounds.naturalHeight)
    ) {
      setHasUnsavedChanges(true)
      setImages((prev) =>
        prev.map((image) =>
          image.id === selectedImageId
            ? {
                ...image,
                naturalWidth: nextBounds.naturalWidth,
                naturalHeight: nextBounds.naturalHeight,
              }
            : image
        )
      )
    }
  }, [selectedImage?.naturalHeight, selectedImage?.naturalWidth, selectedImageId])

  useEffect(() => {
    if (!imageSrc) {
      setImageBounds(EMPTY_IMAGE_BOUNDS)
      return undefined
    }

    refreshImageBounds()
    window.addEventListener('resize', refreshImageBounds)

    return () => {
      window.removeEventListener('resize', refreshImageBounds)
    }
  }, [imageSrc, refreshImageBounds])

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files || [])
    if (!files.length) {
      return
    }

    const validFiles = files.filter((file) => file.type.startsWith('image/'))
    const invalidFileCount = files.length - validFiles.length

    if (!validFiles.length) {
      setFeedbackMessage(
        t('aoiPage.invalidFileType', {
          defaultValue: 'Lutfen sadece gorsel dosyasi yukleyin.',
        }),
        'warning'
      )
      event.target.value = ''
      return
    }

    const nextImages = validFiles.map((file) => {
      const nextImage = createImageItem(file)
      objectUrlsRef.current.add(nextImage.src)
      return nextImage
    })

    setImages((prev) => [...prev, ...nextImages])
    setHasUnsavedChanges(true)
    setSelectedImageId(nextImages[0].id)
    setSelectedAreaId(null)
    setImageBounds(EMPTY_IMAGE_BOUNDS)
    resetDrafts()
    setFeedbackMessage(
      invalidFileCount
        ? t('aoiPage.invalidFilesSkipped', {
            defaultValue: '{{count}} dosya atlandi; sadece gorseller eklendi.',
            count: invalidFileCount,
          })
        : '',
      invalidFileCount ? 'warning' : 'info'
    )
    event.target.value = ''
  }

  const removeImage = (imageId) => {
    const imageIndex = images.findIndex((image) => image.id === imageId)
    if (imageIndex === -1) {
      return
    }

    const targetImage = images[imageIndex]
    const nextImages = images.filter((image) => image.id !== imageId)

    if (targetImage.storagePath) {
      deletedStoragePathsRef.current.add(targetImage.storagePath)
    }

    releaseObjectUrl(targetImage.src)
    setImages(nextImages)
    setHasUnsavedChanges(true)

    if (selectedImageId === imageId) {
      const fallbackImage = nextImages[imageIndex] || nextImages[imageIndex - 1] || null
      setSelectedImageId(fallbackImage?.id || null)
      setSelectedAreaId(null)
      setImageBounds(EMPTY_IMAGE_BOUNDS)
      resetDrafts()
    }

    if (!nextImages.length) {
      setFeedback(null)
    }
  }

  const createAreaFromRect = (rect) => {
    if (!selectedImageId) {
      return
    }

    const nextArea = buildRectArea(rect, imageBounds, areas.length)
    setAreasForSelectedImage((prev) => [...prev, nextArea])
    setHasUnsavedChanges(true)
    setSelectedAreaId(nextArea.id)
  }

  const createAreaFromPolygon = (points) => {
    if (!selectedImageId) {
      return
    }

    const nextArea = buildPolygonArea(points, imageBounds, areas.length)
    setAreasForSelectedImage((prev) => [...prev, nextArea])
    setHasUnsavedChanges(true)
    setSelectedAreaId(nextArea.id)
  }

  const getPointerPosition = (event) => {
    const imageRect = imageRef.current?.getBoundingClientRect()
    if (!imageRect) {
      return null
    }

    return {
      x: event.clientX - imageRect.left,
      y: event.clientY - imageRect.top,
    }
  }

  const handleCanvasMouseDown = (event) => {
    if (
      drawingMode !== 'rect' ||
      !imageSrc ||
      !imageBounds.width ||
      event.target !== event.currentTarget
    ) {
      return
    }

    const pointer = getPointerPosition(event)
    if (!pointer) {
      return
    }

    setSelectedAreaId(null)
    setDrawingStart(pointer)
    setDraftRect({ x: pointer.x, y: pointer.y, width: 0, height: 0 })
  }

  const handleCanvasMouseMove = (event) => {
    const pointer = getPointerPosition(event)
    if (!pointer) {
      return
    }

    if (drawingMode === 'polygon') {
      setPolygonCursor(pointer)
    }

    if (!drawingStart || !imageBounds.width) {
      return
    }

    setDraftRect(toDraftRect(drawingStart, pointer, imageBounds))
  }

  const finishDrawing = (event) => {
    if (drawingMode !== 'rect') {
      return
    }

    if (!drawingStart || !draftRect) {
      return
    }

    const pointer = getPointerPosition(event)
    const finalizedRect = pointer
      ? toDraftRect(drawingStart, pointer, imageBounds)
      : draftRect

    if (finalizedRect.width >= MIN_RECT_SIZE && finalizedRect.height >= MIN_RECT_SIZE) {
      createAreaFromRect(finalizedRect)
    }

    setDrawingStart(null)
    setDraftRect(null)
  }

  const finishPolygonDrawing = () => {
    if (draftPolygon.length < MIN_POLYGON_POINTS || !imageBounds.width || !imageBounds.height) {
      return
    }

    createAreaFromPolygon(draftPolygon)
    setDraftPolygon([])
    setPolygonCursor(null)
  }

  const handleCanvasClick = (event) => {
    if (
      drawingMode !== 'polygon' ||
      !imageSrc ||
      !imageBounds.width ||
      event.target !== event.currentTarget
    ) {
      return
    }

    const pointer = getPointerPosition(event)
    if (!pointer) {
      return
    }

    if (
      draftPolygon.length >= MIN_POLYGON_POINTS &&
      isPointNear(pointer, draftPolygon[0])
    ) {
      finishPolygonDrawing()
      return
    }

    setSelectedAreaId(null)
    setDraftPolygon((prev) => [...prev, pointer])
  }

  const toPixels = (area) => ({
    x: area.x * imageBounds.width,
    y: area.y * imageBounds.height,
    width: area.width * imageBounds.width,
    height: area.height * imageBounds.height,
  })

  const toPixelPoints = (points) =>
    points.map((point) => ({
      x: point.x * imageBounds.width,
      y: point.y * imageBounds.height,
    }))

  const handleDrawingModeChange = (nextMode) => {
    setDrawingMode(nextMode)
    resetDrafts()
  }

  const updateArea = (areaId, changes) => {
    setHasUnsavedChanges(true)
    setAreasForSelectedImage((prev) =>
      prev.map((area) => (area.id === areaId ? { ...area, ...changes } : area))
    )
  }

  const updateAreaFromPixels = (areaId, rect) => {
    updateArea(areaId, {
      x: clamp(rect.x / imageBounds.width, 0, 1),
      y: clamp(rect.y / imageBounds.height, 0, 1),
      width: clamp(rect.width / imageBounds.width, 0.01, 1),
      height: clamp(rect.height / imageBounds.height, 0.01, 1),
    })
  }

  const removeArea = (areaId) => {
    setHasUnsavedChanges(true)
    setAreasForSelectedImage((prev) => prev.filter((area) => area.id !== areaId))
    if (selectedAreaId === areaId) {
      setSelectedAreaId(null)
    }
  }

  const clearAreas = () => {
    setHasUnsavedChanges(true)
    setAreasForSelectedImage([])
    setSelectedAreaId(null)
    resetDrafts()
  }

  const persistDefinition = useCallback(async () => {
    if (!currentUser?.uid || isSaving) {
      return
    }

    setIsSaving(true)
    setFeedback(null)

    try {
      const nextDefinitionRef = routeDefinitionId
        ? doc(db, 'aoiDefinitions', routeDefinitionId)
        : doc(collection(db, 'aoiDefinitions'))

      const persistedImages = []

      for (const image of images) {
        let nextImage = image

        if (image.file) {
          const storagePath = [
            'aoiDefinitions',
            currentUser.uid,
            nextDefinitionRef.id,
            `${image.id}_${sanitizeStorageSegment(image.name)}`,
          ].join('/')
          const storageRef = ref(storage, storagePath)

          await uploadBytes(storageRef, image.file)

          nextImage = {
            ...image,
            src: await getDownloadURL(storageRef),
            file: null,
            storagePath,
          }
        }

        persistedImages.push(nextImage)
      }

      const deletedStoragePaths = Array.from(deletedStoragePathsRef.current)

      await Promise.all(
        deletedStoragePaths.map(async (storagePath) => {
          try {
            await deleteObject(ref(storage, storagePath))
          } catch (error) {
            console.error('Silinen AOI gorseli kaldirilamadi:', error)
          }
        })
      )
      deletedStoragePathsRef.current.clear()

      const normalizedTitle = definitionTitle.trim() || createDefinitionTitle()
      const serializableImages = persistedImages.map(serializeImage)

      await setDoc(
        nextDefinitionRef,
        {
          title: normalizedTitle,
          ownerId: currentUser.uid,
          ownerEmail: currentUser.email || '',
          images: serializableImages,
          imageCount: serializableImages.length,
          totalAreaCount: serializableImages.reduce(
            (count, image) => count + image.areas.length,
            0
          ),
          coverImageUrl: serializableImages[0]?.src || '',
          updatedAt: serverTimestamp(),
          ...(!routeDefinitionId ? { createdAt: serverTimestamp() } : {}),
        },
        { merge: true }
      )

      setDefinitionTitle(normalizedTitle)
      setImages((prevImages) =>
        prevImages.map((image) => {
          const persistedImage = persistedImages.find((item) => item.id === image.id)

          if (persistedImage && persistedImage.src !== image.src) {
            releaseObjectUrl(image.src)
          }

          return persistedImage || image
        })
      )
      setHasUnsavedChanges(false)
      setLastSavedAt(new Date())
      setFeedbackMessage(
        t('aoiPage.saveSuccess', {
          defaultValue: 'AOI tanimi kaydedildi.',
        }),
        'success'
      )

      if (!routeDefinitionId) {
        navigate(`/aoi/${nextDefinitionRef.id}`, { replace: true })
      }
    } catch (error) {
      console.error('AOI tanimi kaydedilirken hata:', error)
      setFeedbackMessage(
        t('aoiPage.saveError', {
          defaultValue: 'AOI tanimi kaydedilemedi.',
        }),
        'error'
      )
    } finally {
      setIsSaving(false)
    }
  }, [
    currentUser?.email,
    currentUser?.uid,
    definitionTitle,
    images,
    isSaving,
    navigate,
    releaseObjectUrl,
    routeDefinitionId,
    setFeedbackMessage,
    t,
  ])

  useEffect(() => {
    if (!currentUser?.uid || !hasUnsavedChanges || isSaving || isLoadingDefinition) {
      return undefined
    }

    const saveTimer = window.setTimeout(() => {
      persistDefinition()
    }, 1200)

    return () => {
      window.clearTimeout(saveTimer)
    }
  }, [
    currentUser?.uid,
    hasUnsavedChanges,
    isLoadingDefinition,
    isSaving,
    persistDefinition,
  ])

  const exportTextFile = (content, fileName, successMessage, errorMessage) => {
    try {
      downloadTextFile(content, fileName)
      setFeedbackMessage(successMessage, 'success')
    } catch (error) {
      setFeedbackMessage(errorMessage, 'error')
    }
  }

  const exportSelectedImage = () => {
    if (!selectedImage) {
      return
    }

    const areaLines = buildAreaExportLines(selectedImage)
    if (!areaLines.length) {
      setFeedbackMessage(
        t('aoiPage.noAreaForSelectedExport', {
          defaultValue: 'Aktif resimde export edilecek AOI bulunamadi.',
        }),
        'warning'
      )
      return
    }

    exportTextFile(
      areaLines.join('\n'),
      toExportFileName(selectedImage.name),
      t('aoiPage.exportSelectedSuccess', {
        defaultValue: 'Aktif resmin AOI tanimlari TXT olarak indirildi.',
      }),
      t('aoiPage.exportTxtError', {
        defaultValue: 'TXT export olusturulamadi.',
      })
    )
  }

  const exportAllImages = () => {
    if (!images.length) {
      return
    }

    let exportedAreaCount = 0
    const areaLines = images.flatMap((image) => {
      const lines = buildAreaExportLines(image, exportedAreaCount)
      exportedAreaCount += lines.length
      return lines
    })

    if (!areaLines.length) {
      setFeedbackMessage(
        t('aoiPage.noAreaForAllExport', {
          defaultValue: 'Export edilecek AOI bulunamadi.',
        }),
        'warning'
      )
      return
    }

    exportTextFile(
      areaLines.join('\n'),
      'aoi_polygon.txt',
      t('aoiPage.exportAllSuccess', {
        defaultValue: 'Tum AOI tanimlari TXT olarak indirildi.',
      }),
      t('aoiPage.exportTxtError', {
        defaultValue: 'TXT export olusturulamadi.',
      })
    )
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tagName = event.target?.tagName
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        return
      }

      if (event.key === 'Escape') {
        resetDrafts()
      }

      if (
        event.key === 'Enter' &&
        drawingMode === 'polygon' &&
        draftPolygon.length >= MIN_POLYGON_POINTS &&
        imageBounds.width &&
        imageBounds.height &&
        selectedImageId
      ) {
        const nextArea = buildPolygonArea(
          draftPolygon,
          { width: imageBounds.width, height: imageBounds.height },
          areas.length
        )

        setAreasForSelectedImage((prev) => [...prev, nextArea])
        setHasUnsavedChanges(true)
        setSelectedAreaId(nextArea.id)
        setDraftPolygon([])
        setPolygonCursor(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    areas.length,
    draftPolygon,
    drawingMode,
    imageBounds.height,
    imageBounds.width,
    resetDrafts,
    selectedImageId,
    setAreasForSelectedImage,
  ])

  const polygonPreviewPoints =
    draftPolygon.length && polygonCursor ? [...draftPolygon, polygonCursor] : draftPolygon

  const selectedAreaSummary = selectedArea
    ? selectedArea.shape === 'polygon'
      ? `${selectedArea.points.length} nokta / ${Math.round(selectedArea.width * 100)}% x ${Math.round(selectedArea.height * 100)}%`
      : `${Math.round(selectedArea.x * 100)}%, ${Math.round(selectedArea.y * 100)}% / ${Math.round(selectedArea.width * 100)}% x ${Math.round(selectedArea.height * 100)}%`
    : ''

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: 'rgba(248, 250, 252, 0.96)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2, md: 4 },
          py: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {pageTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('aoiPage.subtitle', {
                defaultValue:
                  'Gorsel yukleyin, AOI alanlarini cizin, kaydedin ve dashboarddan tekrar acin.',
              })}
            </Typography>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ flexWrap: 'wrap', useFlexGap: true }}
          >
            <Chip
              size="small"
              label={
                imageName ||
                t('aoiPage.noImageSelected', { defaultValue: 'Henüz resim secilmedi' })
              }
              color={imageName ? 'primary' : 'default'}
              variant={imageName ? 'filled' : 'outlined'}
            />
            <Chip
              size="small"
              variant="outlined"
              label={t('aoiPage.imageCount', {
                defaultValue: '{{count}} resim',
                count: images.length,
              })}
            />
            <Chip
              size="small"
              variant="outlined"
              label={t('aoiPage.totalAreaCount', {
                defaultValue: '{{count}} AOI',
                count: totalAreaCount,
              })}
            />
            <Chip
              size="small"
              color={isSaving ? 'primary' : hasUnsavedChanges ? 'warning' : 'success'}
              variant={isSaving ? 'filled' : 'outlined'}
              label={
                isSaving
                  ? t('aoiPage.saving', { defaultValue: 'Kaydediliyor...' })
                  : hasUnsavedChanges
                    ? t('aoiPage.unsavedChanges', { defaultValue: 'Kaydedilmedi' })
                    : lastSavedAt
                      ? t('aoiPage.savedAt', {
                          defaultValue: 'Kayitli · {{value}}',
                          value: formatSavedAt(lastSavedAt, i18n.language),
                        })
                      : t('aoiPage.newRecord', { defaultValue: 'Yeni kayit' })
              }
            />
            <Button
              variant="contained"
              size="small"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />}
              disabled={isSaving || isLoadingDefinition}
              onClick={persistDefinition}
            >
              {t('aoiPage.saveDefinition', { defaultValue: 'Kaydet' })}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={() => navigate('/ProjectsPage')}
            >
              {t('aoiPage.backToDashboard', { defaultValue: 'Dashboard' })}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Stack spacing={2}>
          <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <TextField
                  label={t('aoiPage.definitionTitle', { defaultValue: 'Kayit adi' })}
                  value={definitionTitle}
                  onChange={(event) => {
                    setDefinitionTitle(event.target.value)
                    setHasUnsavedChanges(true)
                  }}
                  fullWidth
                  placeholder={t('aoiPage.definitionTitlePlaceholder', {
                    defaultValue: 'Ornek: Ambalaj test AOI tanimi',
                  })}
                />
                <Button
                  variant="contained"
                  size="large"
                  startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveAltIcon />}
                  disabled={isSaving || isLoadingDefinition}
                  onClick={persistDefinition}
                  sx={{ minWidth: { md: 168 } }}
                >
                  {t('aoiPage.saveDefinition', { defaultValue: 'Kaydet' })}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {feedback?.message ? (
            <Alert severity={feedback.severity || 'info'} sx={{ borderRadius: 2 }}>
              {feedback.message}
            </Alert>
          ) : null}

          {isLoadingDefinition ? (
            <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
              <CardContent
                sx={{
                  minHeight: 240,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <CircularProgress />
                  <Typography color="text.secondary">
                    {t('aoiPage.loadingDefinition', {
                      defaultValue: 'Kayitli AOI tanimi yukleniyor...',
                    })}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
              <Card
                variant="outlined"
                sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ mb: 2, flexWrap: 'wrap', useFlexGap: true }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddPhotoAlternateIcon />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {t('aoiPage.uploadImages', { defaultValue: 'Resim yukle' })}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="inherit"
                      startIcon={<RestartAltIcon />}
                      disabled={!areas.length}
                      onClick={clearAreas}
                    >
                      {t('aoiPage.clearAreas', { defaultValue: 'Temizle' })}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SaveAltIcon />}
                      disabled={!selectedImage}
                      onClick={exportSelectedImage}
                    >
                      {t('aoiPage.exportSelectedTxt', { defaultValue: 'Aktif TXT' })}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SaveAltIcon />}
                      disabled={!images.length}
                      onClick={exportAllImages}
                    >
                      {t('aoiPage.exportAllTxt', { defaultValue: 'Tum TXT' })}
                    </Button>
                  </Stack>

                  {images.length ? (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {t('aoiPage.imageManagerTitle', { defaultValue: 'Yuklenen gorseller' })}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
                        {images.map((image) => {
                          const isActive = image.id === selectedImageId

                          return (
                            <Box
                              key={image.id}
                              onClick={() => selectImage(image.id)}
                              sx={{
                                minWidth: 132,
                                maxWidth: 132,
                                p: 0.75,
                                borderRadius: 1.5,
                                border: isActive ? '2px solid #1976d2' : '1px solid #e2e8f0',
                                backgroundColor: isActive ? '#eff6ff' : '#ffffff',
                                boxShadow: 'none',
                                cursor: 'pointer',
                                position: 'relative',
                                flexShrink: 0,
                              }}
                            >
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  removeImage(image.id)
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: 6,
                                  right: 6,
                                  backgroundColor: 'rgba(255,255,255,0.92)',
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>

                              <Box
                                component="img"
                                src={image.src}
                                alt={image.name}
                                sx={{
                                  width: '100%',
                              height: 60,
                              objectFit: 'cover',
                              display: 'block',
                              borderRadius: 1.5,
                              backgroundColor: '#0f172a',
                              mb: 1,
                            }}
                          />

                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {image.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('aoiPage.areaCount', {
                              defaultValue: '{{count}} alan',
                              count: image.areas.length,
                            })}
                          </Typography>
                          {image.naturalWidth ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {`${image.naturalWidth} x ${image.naturalHeight}px`}
                            </Typography>
                          ) : null}
                        </Box>
                      )
                    })}
                  </Stack>
                </Stack>
              ) : null}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 2 }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant={drawingMode === 'rect' ? 'contained' : 'outlined'}
                    color={drawingMode === 'rect' ? 'primary' : 'inherit'}
                    startIcon={<CropSquareIcon />}
                    disabled={!imageSrc}
                    onClick={() => handleDrawingModeChange('rect')}
                    size="small"
                  >
                    {t('aoiPage.rectMode', { defaultValue: 'Dikdortgen' })}
                  </Button>
                  <Button
                    variant={drawingMode === 'polygon' ? 'contained' : 'outlined'}
                    color={drawingMode === 'polygon' ? 'primary' : 'inherit'}
                    startIcon={<ChangeHistoryIcon />}
                    disabled={!imageSrc}
                    onClick={() => handleDrawingModeChange('polygon')}
                    size="small"
                  >
                    {t('aoiPage.polygonMode', { defaultValue: 'Polygon' })}
                  </Button>
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ flexWrap: 'wrap', useFlexGap: true }}
                >
                  <Chip
                    color={drawingMode === 'polygon' ? 'secondary' : 'primary'}
                    variant="outlined"
                    label={
                      drawingMode === 'polygon'
                        ? t('aoiPage.polygonHint', {
                            defaultValue:
                              'Polygon: Noktalari tiklayin, ilk noktaya tiklayin veya Enter ile bitirin.',
                          })
                        : t('aoiPage.rectHint', {
                            defaultValue: 'Dikdortgen: Bos alanda surukleyerek cizin.',
                          })
                    }
                  />
                  {drawingMode === 'polygon' && draftPolygon.length ? (
                    <Chip
                      variant="outlined"
                      label={t('aoiPage.polygonPointCount', {
                        defaultValue: '{{count}} nokta',
                        count: draftPolygon.length,
                      })}
                    />
                  ) : null}
                </Stack>
              </Stack>

              {drawingMode === 'polygon' && draftPolygon.length ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    disabled={draftPolygon.length < MIN_POLYGON_POINTS}
                    onClick={finishPolygonDrawing}
                  >
                    {t('aoiPage.finishPolygon', { defaultValue: 'Polygoni tamamla' })}
                  </Button>
                  <Button variant="outlined" color="inherit" onClick={resetDrafts}>
                    {t('aoiPage.cancelPolygon', { defaultValue: 'Taslagi iptal et' })}
                  </Button>
                </Stack>
              ) : null}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleImageUpload}
              />

              {!imageSrc ? (
                <Box
                  sx={{
                    minHeight: 520,
                    border: '1px dashed #cbd5e1',
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: '#f8fafc',
                    textAlign: 'center',
                    px: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t('aoiPage.emptyStateTitle', {
                        defaultValue: 'AOI tanimi icin bir veya daha fazla gorsel secin',
                      })}
                    </Typography>
                    <Typography color="text.secondary">
                      {t('aoiPage.emptyStateBody', {
                        defaultValue:
                          'PNG, JPG veya benzeri gorseller yukledikten sonra listedeki aktif gorsel uzerinde dikdortgen ya da polygon alanlari fare ile cizebilirsiniz.',
                      })}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: 520,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    backgroundColor: '#0f172a',
                    borderRadius: 2,
                    p: 1.5,
                    overflow: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      lineHeight: 0,
                      maxWidth: '100%',
                    }}
                  >
                    <Box
                      component="img"
                      ref={imageRef}
                      src={imageSrc}
                      alt={imageName || 'AOI source'}
                      onLoad={refreshImageBounds}
                      sx={{
                        display: 'block',
                        maxWidth: '100%',
                        maxHeight: '72vh',
                        objectFit: 'contain',
                        borderRadius: 2,
                        userSelect: 'none',
                      }}
                    />

                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        cursor: imageSrc ? 'crosshair' : 'default',
                      }}
                      onMouseDown={handleCanvasMouseDown}
                      onMouseMove={handleCanvasMouseMove}
                      onMouseUp={finishDrawing}
                      onMouseLeave={finishDrawing}
                      onClick={handleCanvasClick}
                    >
                      <Box
                        component="svg"
                        viewBox={`0 0 ${imageBounds.width || 1} ${imageBounds.height || 1}`}
                        preserveAspectRatio="none"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                          overflow: 'visible',
                        }}
                      >
                        {areas
                          .filter((area) => area.shape === 'polygon')
                          .map((area, index) => {
                            const points = toPixelPoints(area.points)
                            const bounds = getPolygonBounds(points)
                            const isSelected = area.id === selectedAreaId

                            return (
                              <g key={area.id}>
                                <polygon
                                  points={toSvgPointString(points)}
                                  fill={`${area.color}22`}
                                  stroke={area.color}
                                  strokeWidth={2}
                                  style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                                  onMouseDown={(event) => {
                                    event.stopPropagation()
                                    setSelectedAreaId(area.id)
                                  }}
                                />
                                <rect
                                  x={bounds.x}
                                  y={bounds.y}
                                  width={Math.min(Math.max(bounds.width, 84), imageBounds.width - bounds.x)}
                                  height={24}
                                  rx={6}
                                  fill={area.color}
                                  style={{ pointerEvents: 'none' }}
                                />
                                <text
                                  x={bounds.x + 8}
                                  y={bounds.y + 16}
                                  fill="#fff"
                                  fontSize="12"
                                  fontWeight="700"
                                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                                >
                                  {area.name || `AOI ${index + 1}`}
                                </text>
                                {isSelected ? (
                                  <polygon
                                    points={toSvgPointString(points)}
                                    fill="none"
                                    stroke={area.color}
                                    strokeWidth={5}
                                    strokeOpacity={0.3}
                                    style={{ pointerEvents: 'none' }}
                                  />
                                ) : null}
                              </g>
                            )
                          })}

                        {polygonPreviewPoints.length >= 2 ? (
                          <polyline
                            points={toSvgPointString(polygonPreviewPoints)}
                            fill="none"
                            stroke="#ffffff"
                            strokeWidth={2}
                            strokeDasharray="6 4"
                            style={{ pointerEvents: 'none' }}
                          />
                        ) : null}

                        {draftPolygon.map((point, index) => (
                          <circle
                            key={`draft-point-${index}`}
                            cx={point.x}
                            cy={point.y}
                            r={index === 0 && draftPolygon.length >= MIN_POLYGON_POINTS ? 6 : 4}
                            fill={index === 0 ? '#facc15' : '#ffffff'}
                            stroke="#0f172a"
                            strokeWidth={2}
                            style={{ pointerEvents: 'none' }}
                          />
                        ))}
                      </Box>

                      {areas.map((area, index) => {
                        if (area.shape === 'polygon') {
                          return null
                        }

                        const rect = toPixels(area)
                        const isSelected = area.id === selectedAreaId

                        return (
                          <Rnd
                            key={area.id}
                            bounds="parent"
                            size={{ width: rect.width, height: rect.height }}
                            position={{ x: rect.x, y: rect.y }}
                            minWidth={MIN_RECT_SIZE}
                            minHeight={MIN_RECT_SIZE}
                            onDragStart={() => setSelectedAreaId(area.id)}
                            onDragStop={(event, data) => {
                              updateAreaFromPixels(area.id, {
                                x: data.x,
                                y: data.y,
                                width: rect.width,
                                height: rect.height,
                              })
                            }}
                            onResizeStart={() => setSelectedAreaId(area.id)}
                            onResizeStop={(event, direction, ref, delta, position) => {
                              updateAreaFromPixels(area.id, {
                                x: position.x,
                                y: position.y,
                                width: Number.parseFloat(ref.style.width),
                                height: Number.parseFloat(ref.style.height),
                              })
                            }}
                          >
                            <Box
                              onMouseDown={(event) => {
                                event.stopPropagation()
                                setSelectedAreaId(area.id)
                              }}
                              sx={{
                                width: '100%',
                                height: '100%',
                                border: `2px solid ${area.color}`,
                                backgroundColor: `${area.color}22`,
                                borderRadius: 1,
                                boxSizing: 'border-box',
                                boxShadow: isSelected ? `0 0 0 2px ${area.color}55` : 'none',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1,
                                  py: 0.5,
                                  backgroundColor: area.color,
                                  color: '#fff',
                                  fontSize: 12,
                                  fontWeight: 700,
                                  borderBottomRightRadius: 6,
                                }}
                              >
                                {area.name || `AOI ${index + 1}`}
                              </Box>
                            </Box>
                          </Rnd>
                        )
                      })}

                      {draftRect ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: draftRect.x,
                            top: draftRect.y,
                            width: draftRect.width,
                            height: draftRect.height,
                            border: '2px dashed #ffffff',
                            backgroundColor: 'rgba(255,255,255,0.12)',
                            pointerEvents: 'none',
                            borderRadius: 1,
                          }}
                        />
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card
            variant="outlined"
            sx={{ width: { xs: '100%', lg: 320 }, borderRadius: 2, boxShadow: 'none' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                {t('aoiPage.sidebarTitle', { defaultValue: 'AOI detaylari' })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('aoiPage.sidebarHint', {
                  defaultValue: 'Secili AOI bilgilerini duzenleyin veya alan listesinden secim yapin.',
                })}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2, flexWrap: 'wrap', useFlexGap: true }}
              >
                <Chip
                  label={t('aoiPage.areaCount', {
                    defaultValue: '{{count}} alan',
                    count: areas.length,
                  })}
                  color="primary"
                  variant="outlined"
                />
                {totalAreaCount !== areas.length ? (
                  <Chip
                    label={t('aoiPage.totalAreaCount', {
                      defaultValue: 'Toplam {{count}} alan',
                      count: totalAreaCount,
                    })}
                    variant="outlined"
                  />
                ) : null}
                {selectedImage?.naturalWidth ? (
                  <Chip
                    label={`${selectedImage.naturalWidth} x ${selectedImage.naturalHeight}px`}
                    variant="outlined"
                  />
                ) : null}
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {selectedArea ? (
                <Stack spacing={2}>
                  <TextField
                    label={t('aoiPage.areaName', { defaultValue: 'Alan adi' })}
                    value={selectedArea.name}
                    onChange={(event) => updateArea(selectedArea.id, { name: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    label={t('aoiPage.areaDescription', { defaultValue: 'Aciklama' })}
                    value={selectedArea.description}
                    onChange={(event) =>
                      updateArea(selectedArea.id, { description: event.target.value })
                    }
                    fullWidth
                    multiline
                    minRows={3}
                  />
                  <TextField
                    label={t('aoiPage.areaColor', { defaultValue: 'Renk' })}
                    type="color"
                    value={selectedArea.color}
                    onChange={(event) => updateArea(selectedArea.id, { color: event.target.value })}
                    fullWidth
                    sx={{ '& input': { minHeight: 48, p: 1 } }}
                  />

                  <Alert severity="success" sx={{ alignItems: 'center' }}>
                    {selectedAreaSummary}
                  </Alert>

                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => removeArea(selectedArea.id)}
                  >
                    {t('aoiPage.deleteArea', { defaultValue: 'Secili alani sil' })}
                  </Button>
                </Stack>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('aoiPage.noAreaSelected', {
                    defaultValue:
                      'Duzenlemek icin bir alan secin veya aktif gorselde cizim modunu kullanarak yeni AOI olusturun.',
                  })}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                {t('aoiPage.areaList', { defaultValue: 'Alan listesi' })}
              </Typography>
              <List disablePadding sx={{ maxHeight: 320, overflow: 'auto' }}>
                {areas.length ? (
                  areas.map((area, index) => (
                    <ListItemButton
                      key={area.id}
                      selected={area.id === selectedAreaId}
                      onClick={() => setSelectedAreaId(area.id)}
                      sx={{ borderRadius: 2, mb: 1, border: '1px solid #e2e8f0' }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: area.color,
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      />
                      <ListItemText
                        primary={area.name || `AOI ${index + 1}`}
                        secondary={
                          area.description ||
                          `${area.shape === 'polygon' ? 'Polygon' : 'Dikdortgen'} · ${t('aoiPage.noDescription', {
                            defaultValue: 'Aciklama eklenmedi',
                          })}`
                        }
                      />
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={(event) => {
                          event.stopPropagation()
                          removeArea(area.id)
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('aoiPage.noAreasYet', { defaultValue: 'Henuz AOI eklenmedi.' })}
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
